import { styled } from '@mui/material';
import { css, Theme } from '@mui/material/styles';
import React from 'react';

import Card from '../../base/card';
import { getDesignTokens } from '../../base/core';

interface Props {
  className?: string;
  value: any;
  details: string;
  color?: string;
  description?: React.ReactNode;
  icon?: React.ReactNode;
}

const Details: React.FC<Props> = ({
  className,
  value,
  details,
  color,
  description,
  icon,
}) => {
  return (
    <Card className={className}>
      <div className="stat-header">
        <span className="stat-title">{details}</span>
        {icon && <div className="stat-icon">{icon}</div>}
      </div>
      <div className="stat-value">{value}</div>
      {description && <div className="stat-change">{description}</div>}
    </Card>
  );
};

export default styled(Details)((props: Props & { theme: Theme }) => {
  const t = getDesignTokens(
    props.theme.palette.mode === 'dark' ? 'dark' : 'light'
  );
  return css`
    padding: 20px;
    border: 1px solid ${props.theme.palette.divider};
    border-radius: ${t.radiusMedium}px;
    background: ${props.theme.palette.background.paper};
    box-shadow: ${props.theme.shadows[1]};
    transition: all 0.2s ease;

    &:hover {
      box-shadow: ${props.theme.shadows[2]};
      transform: translateY(-2px);
    }

    .stat-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 16px;
    }

    .stat-title {
      font-family: 'Nunito Sans', sans-serif;
      font-size: 0.8125rem;
      font-weight: 600;
      color: ${props.theme.palette.text.secondary};
      letter-spacing: 0.06em;
    }

    .stat-icon {
      width: 50px;
      height: 50px;
      border-radius: ${t.radiusSmall}px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;
      color: ${t.accent};
      background: ${props.theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.06)'
        : 'rgba(0, 0, 0, 0.04)'};
      border: 1px solid
        ${props.theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.08)'
          : 'rgba(0, 0, 0, 0.06)'};

      svg {
        width: 24px;
        height: 24px;
        fill: currentColor;
        stroke: currentColor;
      }
    }

    .stat-value {
      font-family: 'Nunito Sans', sans-serif;
      font-size: clamp(1.125rem, 1.8vw, 1.5rem);
      font-weight: 700;
      color: ${props.color ?? props.theme.palette.text.primary};
      margin-bottom: 8px;
    }

    .stat-change {
      font-size: 14px;
      font-weight: 500;
    }
  `;
});
